import React, { useEffect, useMemo, useRef } from 'react';
import { useSetResolved } from 'src/core/engine/router/useSetResolved';
import { CmsLayoutOneColumn } from 'src/components/CmsPage/CmsLayoutOneColumn';

import { Wrapper, Container } from '../Layout';

import { CmsLayoutDefault } from './CmsLayoutDefault';
import { CmsLayoutSidebar } from './CmsLayoutSidebar';

export type CmsPageInnerProps = {
    title: string | null;
    content_heading: string | null;
    content: string;
    page_layout: string | CmsLayout;
};

export enum CmsLayout {
    OneColumn = '1column',
    TwoColumnsLeft = '2columns-left',
    TwoColumnsRight = '2columns-right',
}

export const CmsPageInner: React.FC<CmsPageInnerProps> = React.memo((props) => {
    const cmsRef = useRef<HTMLDivElement>(null);

    useSetResolved();

    /**
     * OneTrust only binds click handlers to `.ot-sdk-show-settings` at initial page load.
     * When this CMS content is inserted client-side (SPA nav), ensure clicks still open
     * the preference center by delegating to the OneTrust/Optanon API.
     */
    useEffect(() => {
        const root = cmsRef.current;
        if (!root) return;

        const handler = (event: Event) => {
            const target = (event.target as HTMLElement)?.closest('.ot-sdk-show-settings');
            if (!target) return;
            event.preventDefault();
            const win = window as any;
            if (win?.OneTrust?.ToggleInfoDisplay) {
                win.OneTrust.ToggleInfoDisplay();
            } else if (win?.Optanon?.ToggleInfoDisplay) {
                win.Optanon.ToggleInfoDisplay();
            }
        };

        root.addEventListener('click', handler);
        return () => root.removeEventListener('click', handler);
    }, []);

    const inner = useMemo(() => {
        switch (props.page_layout) {
            case CmsLayout.TwoColumnsRight:
            case CmsLayout.TwoColumnsLeft: {
                return <CmsLayoutSidebar {...props} />;
            }
            case CmsLayout.OneColumn: {
                return <CmsLayoutOneColumn {...props} />;
            }
            default: {
                return <CmsLayoutDefault {...props} />;
            }
        }
    }, [props]);

    return (
        <Wrapper variant="widget-wrapper">
            <Container>
                <div data-test-id="CmsPageInner" ref={cmsRef}>
                    {inner}
                </div>
            </Container>
        </Wrapper>
    );
});
